/**
 * Runtime configuration loader.
 *
 * Reads `window.__RUNTIME_CONFIG__` first (injected at container start via env.js — research R1),
 * falling back to `import.meta.env.VITE_*` for local `vite dev`. Produces a normalized
 * RuntimeConfig; fail-fast validation is applied separately by config/index.ts (T011).
 *
 * Env-key → field mapping is defined in contracts/environment-config.md.
 */

import type {
  RuntimeConfig,
  Environment,
  AuthMode,
  OidcSettings,
  MockUser,
  MonitoredService,
} from './types';

/** Parse a JSON object string into a string map; returns {} on any failure. */
function parseApiBaseUrls(raw: unknown): Record<string, string> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    // Already an object (window.__RUNTIME_CONFIG__ path).
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parseApiBaseUrls(parsed);
      }
    } catch {
      // Fall through — validation will flag the empty/invalid map.
    }
  }
  return {};
}

/** Coerce a value to a non-empty trimmed string, or undefined. */
function str(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const t = v.trim();
    return t === '' ? undefined : t;
  }
  return undefined;
}

/**
 * Parse the monitored-services list. Accepts an already-parsed array
 * (window.__RUNTIME_CONFIG__ path) or a JSON array string (VITE_* path). Entries are
 * carried through with minimal coercion so validation.ts can fail-fast on bad shapes.
 *
 * Returns `undefined` only when the field is truly absent (so the dashboard derives a
 * default set); a present-but-unparseable value yields an empty array, which validation
 * rejects with "must be a non-empty array".
 */
function parseServices(raw: unknown): MonitoredService[] | undefined {
  let arr: unknown = raw;

  if (typeof raw === 'string') {
    if (raw.trim() === '') return undefined;
    try {
      arr = JSON.parse(raw);
    } catch {
      return []; // present but invalid → validation flags it
    }
  }

  if (arr === undefined || arr === null) return undefined;
  if (!Array.isArray(arr)) return []; // present but not an array → validation flags it

  return arr.map((entry) => {
    const e = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>;
    const service: MonitoredService = {
      id: str(e.id) ?? '',
      displayName: str(e.displayName) ?? '',
      target: str(e.target) ?? '',
    };
    const healthPath = str(e.healthPath);
    if (healthPath !== undefined) service.healthPath = healthPath;
    return service;
  });
}

/**
 * Coerce a value to a number, or undefined when truly absent. A present-but-unparseable
 * value yields NaN so validation.ts can fail-fast (rather than silently using the default).
 */
function num(v: unknown): number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return undefined;
}

interface RawSource {
  environment?: unknown;
  authMode?: unknown;
  apiBaseUrls?: unknown;
  defaultApiKey?: unknown;
  oidcAuthority?: unknown;
  oidcClientId?: unknown;
  oidcCognitoDomain?: unknown;
  oidcScope?: unknown;
  oidcRedirectUri?: unknown;
  oidcPostLogoutRedirectUri?: unknown;
  mockUserEmail?: unknown;
  mockUserName?: unknown;
  services?: unknown;
  livenessRefreshIntervalMs?: unknown;
  apiExplorerTarget?: unknown;
  devProxy?: unknown;
}

/**
 * Coerce a runtime value to a boolean: boolean `true` or the string `'true'` → true; anything
 * else (including absent, other strings, non-booleans) → false. No error path — a safe default.
 */
function bool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
  return false;
}

function normalize(src: RawSource): RuntimeConfig {
  const environment = (str(src.environment) as Environment) || 'local';
  const authMode = (str(src.authMode) as AuthMode) || 'mock';
  const apiBaseUrls = parseApiBaseUrls(src.apiBaseUrls);
  const defaultApiKey = str(src.defaultApiKey) || '';

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const config: RuntimeConfig = {
    environment,
    authMode,
    apiBaseUrls,
    defaultApiKey,
    devProxy: bool(src.devProxy),
  };

  // Liveness dashboard: monitored services + auto-refresh interval (both optional).
  const services = parseServices(src.services);
  if (services !== undefined) config.services = services;
  const refreshInterval = num(src.livenessRefreshIntervalMs);
  if (refreshInterval !== undefined) config.livenessRefreshIntervalMs = refreshInterval;

  // API Route Explorer: which named backend the page targets (feature 003). Optional.
  const apiExplorerTarget = str(src.apiExplorerTarget);
  if (apiExplorerTarget !== undefined) config.apiExplorerTarget = apiExplorerTarget;

  // Build OIDC block when any OIDC value is present (validation enforces completeness).
  const authority = str(src.oidcAuthority);
  const clientId = str(src.oidcClientId);
  if (authMode === 'oidc' || authority || clientId) {
    const oidc: OidcSettings = {
      authority: authority || '',
      clientId: clientId || '',
      cognitoDomain: str(src.oidcCognitoDomain) || '',
      scope: str(src.oidcScope) || 'openid email profile',
      redirectUri: str(src.oidcRedirectUri) || `${origin}/`,
      postLogoutRedirectUri: str(src.oidcPostLogoutRedirectUri) || `${origin}/`,
    };
    config.oidc = oidc;
  }

  // Mock user seed (local convenience — defaults keep mock auth runnable out of the box).
  if (authMode === 'mock' || src.mockUserEmail || src.mockUserName) {
    const mockUser: MockUser = {
      email: str(src.mockUserEmail) || 'dev@example.com',
      name: str(src.mockUserName) || 'Local Developer',
    };
    config.mockUser = mockUser;
  }

  return config;
}

/** Read the window-injected runtime config, or null if absent/empty (local vite dev). */
function fromWindow(): RuntimeConfig | null {
  if (typeof window === 'undefined') return null;
  const raw = window.__RUNTIME_CONFIG__;
  if (!raw || typeof raw !== 'object' || Object.keys(raw).length === 0) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const oidc = (r.oidc ?? {}) as Record<string, unknown>;
  const mockUser = (r.mockUser ?? {}) as Record<string, unknown>;
  return normalize({
    environment: r.environment,
    authMode: r.authMode,
    apiBaseUrls: r.apiBaseUrls,
    defaultApiKey: r.defaultApiKey,
    oidcAuthority: oidc.authority,
    oidcClientId: oidc.clientId,
    oidcCognitoDomain: oidc.cognitoDomain,
    oidcScope: oidc.scope,
    oidcRedirectUri: oidc.redirectUri,
    oidcPostLogoutRedirectUri: oidc.postLogoutRedirectUri,
    mockUserEmail: mockUser.email,
    mockUserName: mockUser.name,
    services: r.services,
    livenessRefreshIntervalMs: r.livenessRefreshIntervalMs,
    apiExplorerTarget: r.apiExplorerTarget,
    devProxy: r.devProxy,
  });
}

/** Read config from Vite env (.env) for local `vite dev`. */
function fromViteEnv(): RuntimeConfig {
  const env = import.meta.env;
  return normalize({
    environment: env.VITE_APP_ENVIRONMENT,
    authMode: env.VITE_AUTH_MODE,
    apiBaseUrls: env.VITE_API_BASE_URLS,
    defaultApiKey: env.VITE_DEFAULT_API_KEY,
    oidcAuthority: env.VITE_OIDC_AUTHORITY,
    oidcClientId: env.VITE_OIDC_CLIENT_ID,
    oidcCognitoDomain: env.VITE_OIDC_COGNITO_DOMAIN,
    oidcScope: env.VITE_OIDC_SCOPE,
    oidcRedirectUri: env.VITE_OIDC_REDIRECT_URI,
    oidcPostLogoutRedirectUri: env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI,
    mockUserEmail: env.VITE_MOCK_USER_EMAIL,
    mockUserName: env.VITE_MOCK_USER_NAME,
    services: env.VITE_LIVENESS_SERVICES,
    livenessRefreshIntervalMs: env.VITE_LIVENESS_REFRESH_INTERVAL_MS,
    apiExplorerTarget: env.VITE_API_EXPLORER_TARGET,
    devProxy: env.VITE_DEV_PROXY,
  });
}

/**
 * Load the runtime configuration, preferring window-injected config over Vite env.
 */
export function loadRuntimeConfig(): RuntimeConfig {
  return fromWindow() ?? fromViteEnv();
}
